import tenantConstants from '@constants';
import tenantUtils from '@utils';
import { t } from 'i18next';
import React, { useMemo } from 'react';
import QRCode from 'react-qr-code';
import { useSelector } from 'react-redux';
import Styled from 'styled-components';
import { DrawerModal, Icon } from '../../../../components/common';
import { regaQRCodeClickEvent } from '../../../../services/analyticsService';
import { formatNumberString, isHttpUrl } from '../../../../utility/utility';
import RegaDetailFields from './RegaDetailFields';

/* Top REGA summary band (Figma): REGA Details / type / price · license · location · area · QR. */
const Band = Styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  height: 128.39px;
  padding: 24px 68px;
  background: linear-gradient(180deg, #F2FAFA 0%, #FFFFFF 154.69%);

  @media (max-width: 767px) {
    flex-direction: column;
    height: auto;
    padding: 16px;
    gap: 16px;
  }

  .label { color: #707070; font-size: 12px; font-weight: 700; line-height: 18px; margin: 0 0 6px; }
  .type { color: #000000E0; font-size: 14px; font-weight: 700; line-height: 20px; margin: 0 0 6px; }
  .price { color: #006169; font-size: 24px; font-weight: 700; line-height: 30px; margin: 0;
           display: flex; align-items: center; gap: 6px; }
  .price svg { width: 18px; height: 22px; }

  .info { display: flex; flex-direction: column; gap: 12px; }
  .info .row { display: flex; align-items: center; gap: 8px; color: #222222; font-size: 14px; line-height: 20px; }
  .info .row .lbl { color: #707070; }
  .info .row .val { color: #222222; font-weight: 600; }

  .qr { display: flex; flex-direction: column; align-items: center; gap: 10px; }
  .rega-link { display: inline-flex; align-items: center; justify-content: center; gap: 4px; color: #006169;
               font-size: 12px; font-weight: 400; white-space: nowrap; }
`;

const ListingDetailModal = ({ setModalVisible, visible, listing }) => {
  const { user } = useSelector((state) => state.app.loginUser);
  const rega = listing?.rega_details;
  const specs = rega?.property_specs;

  const typeLabel = useMemo(
    () =>
      [
        tenantUtils.getLocalisedString(specs, 'listing_type'),
        tenantUtils.getLocalisedString(specs, 'advertisement_type'),
      ]
        .filter(Boolean)
        .join(' '),
    [specs],
  );

  const location = useMemo(
    () =>
      [rega?.location?.district, rega?.location?.city, tenantUtils.getLocalisedString(rega?.location, 'region')]
        .filter(Boolean)
        .join(', '),
    [rega?.location],
  );

  const price = listing?.price ? formatNumberString(listing.price, { maximumFractionDigits: 0 }) : null;
  const area = specs?.area_size;
  const adLicense = rega?.license_info?.ad_license_number;
  const url = rega?.url;

  return (
    <DrawerModal
      visible={visible}
      onCancel={() => setModalVisible(false)}
      title={t('Listing Details from REGA')}
      width="880px"
      styles={{
        body: { maxHeight: 608, overflowY: 'auto', padding: 0 },
        content: { borderRadius: 12, overflow: 'hidden', padding: 0 },
      }}
      footer={null}
      height="664px"
    >
      <Band>
        <div className="summary">
          <p className="label">{t('REGA Details')}</p>
          {typeLabel && <p className="type">{typeLabel}</p>}
          {price && (
            <p className="price">
              {tenantConstants.CURRENCY_SYMBOL()} {price}
            </p>
          )}
        </div>

        <div className="info">
          {adLicense && (
            <div className="row">
              <Icon icon="PiSealCheckFill" color="#006169" size={18} />
              <span>
                <span className="lbl">{t('Ad License:')} </span>
                <span className="val">{adLicense}</span>
              </span>
            </div>
          )}
          {location && (
            <div className="row">
              <Icon icon="FaMapMarkerAlt" color="#006169" size={16} />
              <span>{location}</span>
            </div>
          )}
          {area && (
            <div className="row">
              <Icon icon="PiStackSimpleFill" color="#006169" size={16} />
              <span>
                {area} {t('Sq. M.')}
              </span>
            </div>
          )}
        </div>

        {isHttpUrl(url) && (
          <div className="qr">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => regaQRCodeClickEvent(user, true, rega?.license_info)}
            >
              <QRCode size={56} value={url} viewBox="0 0 256 256" />
            </a>
            <a
              href={isHttpUrl(url) ? url : undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="rega-link"
              onClick={() => regaQRCodeClickEvent(user, false, rega?.license_info)}
            >
              {t('View Details on Rega')}
              <Icon icon="MdArrowOutward" size={14} color="#006169" />
            </a>
          </div>
        )}
      </Band>

      <div style={{ padding: '16px 0 8px' }}>
        <RegaDetailFields listing={listing} />
      </div>
    </DrawerModal>
  );
};

export default ListingDetailModal;
